import { FC } from 'react';
import { Button } from '@/components/ui/button';
import Loader from '@/components/Loader';
import { useGetChangeLog } from '@/services/changeLogService';
import type { ChangeLogAction, ChangeLogTarget } from '@/types/changeLog.types';
import { cn, convertISODateToMMDDYYYY, convertISOToTime } from '@/lib/utils';

const ACTION_LABELS: Record<ChangeLogAction, string> = {
  CREATE: 'Created',
  UPDATE: 'Updated',
  DELETE: 'Removed',
};

const formatDateTime = (iso: string) => `${convertISODateToMMDDYYYY(iso)} ${convertISOToTime(iso)}`;

/** Read-only Change Log tab of the vendor and work-order side panels (ZOHO-1480): who made each change and when. */
export const ChangeLogSection: FC<{ target: ChangeLogTarget; active: boolean; className?: string }> = ({
  target,
  active,
  className,
}) => {
  const { data, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } = useGetChangeLog(target, active);
  const rows = (data?.pages ?? []).flatMap((page) => page.changeLogs);

  return (
    <section className={cn('flex flex-col gap-4 w-full py-6', className)} aria-label="Change log">
      <Loader isLoading={isLoading} className="py-4">
        {isError ? (
          <p className="text-sm text-error">Couldn&apos;t load change log</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-placeholder">No changes recorded yet</p>
        ) : (
          <ul className="flex flex-col gap-4 text-sm">
            {rows.map((row) => (
              <li
                key={row.changeLogId}
                className="flex flex-row justify-between gap-2 p-4 rounded-lg bg-[#F5F5F5]"
                data-testid="change-log-entry"
              >
                <div className="flex flex-col gap-1">
                  <span className="font-semibold text-black">
                    {`${row.changedBy.firstName} ${row.changedBy.lastName}`.trim()}
                  </span>
                  <span className="text-xs text-placeholder">{ACTION_LABELS[row.action]}</span>
                </div>
                <span className="text-xs text-placeholder">{formatDateTime(row.changedAt)}</span>
              </li>
            ))}
          </ul>
        )}
      </Loader>

      {hasNextPage && !isError && (
        <Button
          type="button"
          variant="outline"
          className="self-center"
          isLoading={isFetchingNextPage}
          onClick={() => fetchNextPage()}
        >
          Load more
        </Button>
      )}
    </section>
  );
};
